import React from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { STATUS_CONFIG, RCDS_CONFIG, FTPE_CONFIG, DOS_CONFIG } from './configs';

export const StatusBadge = ({ status, onStatusChange, membre, canChangeStatus = false, isUpdating = false }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.en_attente;
  
  const availableStatuses = [
    { key: 'actif', label: 'Actif', color: 'bg-emerald-500' },
    { key: 'en_attente_connexion', label: 'En attente', color: 'bg-amber-500' },
    { key: 'inactif', label: 'Inactif', color: 'bg-gray-500' },
    { key: 'suspendu', label: 'Suspendu', color: 'bg-red-500' }
  ];
  
  if (!canChangeStatus || !onStatusChange) {
    return (
      <div className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border",
        config.color
      )}>
        <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
        {config.label}
      </div>
    );
  }
  
  const normalizedStatus = ['en_attente', 'en_attente_connexion', 'pending'].includes(status) 
    ? 'en_attente_connexion' : status;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity",
            config.color, isUpdating && "opacity-50"
          )}
          disabled={isUpdating}
        >
          {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-white/80" />}
          {config.label}
          <ChevronDown className="w-3 h-3 ml-0.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-44">
        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">Changer le statut</div>
        <DropdownMenuSeparator />
        {availableStatuses.map(({ key, label, color }) => (
          <DropdownMenuItem 
            key={key} onClick={() => onStatusChange(membre, key)}
            className={cn("flex items-center gap-2", normalizedStatus === key && "bg-gray-100")}
          >
            <span className={cn("w-2 h-2 rounded-full", color)} />
            {label}
            {normalizedStatus === key && <Check className="w-3 h-3 ml-auto text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const RCDSBadge = ({ membre }) => {
  const rcdsStatus = membre.statut_rcds || (membre.has_rcds ? 'actif' : 'non_couvert');
  const config = RCDS_CONFIG[rcdsStatus] || RCDS_CONFIG.non_couvert;
  const Icon = config.icon;
  const compactLabels = { 'RCDS Actif': 'RCDS', 'A renouveler': 'A renouv.', 'Expire': 'Expire', 'RCDS en cours': 'En cours', 'Non couvert': '-' };
  
  if (rcdsStatus === 'non_couvert') return <span className="text-xs text-gray-400">-</span>;
  
  return (
    <div className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap", config.color)}
      title={`${config.label} - ${config.description}`}>
      {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
      <span className="truncate">{compactLabels[config.label] || config.label}</span>
    </div>
  );
};

export const FTPEBadge = ({ membre }) => {
  let ftpeStatus = membre.statut_ftpe;
  if (!ftpeStatus) {
    if (membre.ftpe_actif) ftpeStatus = 'actif';
    else if (membre.ftpe_en_attente) ftpeStatus = 'en_attente';
    else if (membre.adhesion_ftpe) ftpeStatus = 'actif';
    else ftpeStatus = 'non_adherent';
  }
  const config = FTPE_CONFIG[ftpeStatus] || FTPE_CONFIG.non_adherent;
  const Icon = config.icon;
  const daysLeft = membre.ftpe_days_until_expiry;
  
  if (ftpeStatus === 'non_adherent') return <span className="text-xs text-gray-400">-</span>;

  const isActive = ['adherent', 'actif', 'signed', 'completed', 'active'].includes(ftpeStatus);
  let expiryLabel = null;
  let expiryColor = '';
  if (isActive && daysLeft != null) {
    if (daysLeft <= 0) { expiryLabel = 'Expire'; expiryColor = 'bg-red-500 text-white'; }
    else if (daysLeft <= 30) { expiryLabel = `${daysLeft}j`; expiryColor = 'bg-red-100 text-red-700 border-red-300'; }
    else if (daysLeft <= 90) { expiryLabel = `${daysLeft}j`; expiryColor = 'bg-amber-100 text-amber-700 border-amber-300'; }
    else {
      const dateFin = membre.ftpe_date_fin ? new Date(membre.ftpe_date_fin) : null;
      if (dateFin) {
        expiryLabel = dateFin.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
        expiryColor = 'text-gray-400';
      }
    }
  }

  const dateFinFormatted = membre.ftpe_date_fin 
    ? new Date(membre.ftpe_date_fin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const titleText = dateFinFormatted 
    ? `${config.label} — Expire le ${dateFinFormatted}${daysLeft != null ? ` (${daysLeft} jours)` : ''}`
    : `${config.label} - ${config.description}`;

  return (
    <div className="flex flex-col items-center gap-0.5" title={titleText}>
      <div className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap", config.color)}>
        {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
        <span className="truncate">{config.label === 'A renouveler' ? 'A renouv.' : config.label}</span>
      </div>
      {expiryLabel && (
        <span className={cn("text-[9px] font-medium px-1.5 py-px rounded border leading-tight", expiryColor)}>{expiryLabel}</span>
      )}
    </div>
  );
};

export const DOSBadge = ({ membre }) => {
  let dosStatus = membre.statut_dos;
  if (!dosStatus) {
    if (membre.dos_paye) dosStatus = 'paye';
    else if (membre.dos_souscrit) dosStatus = 'souscrit';
    else if (membre.dos_en_attente) dosStatus = 'en_attente';
    else if (membre.souscription_dos) {
      dosStatus = membre.souscription_dos.statut_paiement === 'paye' ? 'paye' : 'en_attente';
    } else dosStatus = 'non_souscrit';
  }
  const config = DOS_CONFIG[dosStatus] || DOS_CONFIG.non_souscrit;
  const Icon = config.icon;
  const compactLabels = { 'Souscrit': 'Souscrit', 'Paye': 'Paye', 'En attente': 'En att.', 'Brouillon': 'Brouillon', 'Non souscrit': '-' };
  
  if (dosStatus === 'non_souscrit') return <span className="text-xs text-gray-400">-</span>;
  
  return (
    <div className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap", config.color)}
      title={`DOS-DOMIUM: ${config.label} - ${config.description}`}>
      {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
      <span className="truncate">{compactLabels[config.label] || config.label}</span>
    </div>
  );
};

export const MemberAvatar = ({ membre, size = 'sm' }) => {
  const initials = `${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase();
  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'];
  const colorIndex = (membre.id?.charCodeAt(0) || 0) % colors.length;
  const sizeClasses = { xs: 'h-6 w-6 text-[10px]', sm: 'h-8 w-8 text-xs', default: 'h-9 w-9 text-sm', lg: 'h-11 w-11 text-base' };
  
  return (
    <Avatar className={cn(sizeClasses[size], "flex-shrink-0")}>
      <AvatarFallback className={cn(colors[colorIndex], "text-white font-medium")}>{initials || '??'}</AvatarFallback>
    </Avatar>
  );
};
